// L-11: the "Using AI in hiring" notice a Team Owner confirms the first time
// they open Hiring.
//
// It is not a splash screen. It is what tells the person using an AI hiring
// tool what they are responsible for as its deployer: that Elliott's levels are
// decision support and not the decision, that integrity signals are not proof
// of misconduct, and that informing worker representatives, completing an
// impact assessment and answering explanation requests are theirs to do. Until
// they confirm it the backend refuses every Hiring write (assertAcknowledged).
//
// The wording comes from the server and is under legal review (PRD 8.4), so an
// edit bumps its version and asks every owner again rather than quietly
// claiming they agreed to new terms.

import React, { useState } from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import { ActionButton, BODY, FONT, LINE, SURFACE, TEXT, TEXT_2 } from '../capability-setup/designSystem';
import { Rich } from './hiringParts';
import type { CopyBlock } from '../../../../api/hiring';

const OwnerNoticeDialog: React.FC<{
  open: boolean;
  block: CopyBlock;
  busy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}> = ({ open, block, busy = false, error, onConfirm, onClose }) => {
  const [ticked, setTicked] = useState(false);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: SURFACE, border: `1px solid ${LINE}`, borderRadius: '8px' } }}
    >
      <Box sx={{ p: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, color: TEXT }}>{block.title}</Typography>
        <Box component="ul" sx={{ m: 0, pl: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {block.lines.map((line, i) => (
            <Typography key={i} component="li" sx={{ ...BODY, fontSize: 13, color: TEXT_2 }}>
              <Rich text={line} />
            </Typography>
          ))}
        </Box>
        <Box
          component="label"
          sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer', mt: '4px' }}
        >
          {/* Never pre-ticked: an acknowledgement nobody made is worth nothing. */}
          <Box
            component="input"
            type="checkbox"
            checked={ticked}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTicked(e.target.checked)}
            sx={{ mt: '2px', accentColor: '#F79420', width: 15, height: 15, flexShrink: 0 }}
          />
          <Typography sx={{ ...BODY, color: TEXT_2 }}>
            I have read this and understand my responsibility when I use Hiring.
          </Typography>
        </Box>
        {error && (
          <Typography sx={{ ...BODY, color: '#FF6A55' }} role="alert">
            {error}
          </Typography>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', mt: '4px' }}>
          <ActionButton
            primary
            disabled={!ticked || busy}
            reason={!ticked ? 'Tick the box to confirm you have read it' : undefined}
            onClick={onConfirm}
          >
            {busy ? 'Confirming...' : 'Confirm and Continue'}
          </ActionButton>
        </Box>
      </Box>
    </Dialog>
  );
};

export default OwnerNoticeDialog;
